import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

import { useAppData } from "../context/AppDataContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/formatPrice";
import LanguageSwitcher from "./LanguageSwitcher";
import { localizedCategoryName, localizedProductName } from "../utils/i18nContent";
import { formatStockDisplay } from "../utils/stockFormatter";
import { formatProductAddedDate } from "../utils/dateFormatter";
import {
  FiBell,
  FiSearch,
  FiArrowRight,
  FiChevronDown,
  FiShoppingCart,
  FiUser,
  FiPlusCircle,
  FiX,
  FiMenu,
  FiHeart,
  FiActivity,
  FiMessageSquare,
  FiLogOut,
  FiZap,
  FiChevronRight,
  FiGrid,
} from "react-icons/fi";
import api from "../services/api";
import MobileDrawer from "./MobileDrawer";

const NavbarNotifications = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { unreadNotifications, refreshData } = useAppData();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get("/notifications/?is_read=false");
      setNotifications(res.data.results || res.data);
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error("Failed to fetch notifications", error);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  const toggleDropdown = () => {
    if (!show) fetchNotifications();
    setShow((prev) => !prev);
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/mark-as-read/`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      refreshData();
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}/`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      refreshData();
    } catch (error) {
      console.error("Failed to delete notification", error);
    }
  };

  const handleNotificationSelect = async (notification) => {
    if (!notification.is_read) await markAsRead(notification.id);
    setShow(false);
    if (notification.link) navigate(notification.link);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post("/notifications/mark-all-as-read/");
      setNotifications([]);
      refreshData();
    } catch (error) {
      console.error("Failed to mark all as read", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setShow(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) return;
    const timer = window.setInterval(() => {
      fetchNotifications();
      refreshData();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [user, fetchNotifications, refreshData]);

  if (!user) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-center w-11 h-11 bg-white border border-black/[0.07] rounded-full text-theme-text hover:text-chafadi-primary hover:border-chafadi-primary/40 transition-all relative shadow-sm hover:shadow-md active:scale-90"
        aria-label={t("navbar.notifications")}
      >
        <FiBell size={18} />
        {unreadNotifications > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-chafadi-primary text-white text-[9px] font-bold rounded-full border-2 border-white">
            {unreadNotifications}
          </span>
        )}
      </button>

      {show && (
        <div className="absolute right-0 mt-4 w-96 bg-white rounded-[32px] shadow-3xl border border-component-border overflow-hidden z-[1000] animate-zoom-in">
          <div className="flex items-center justify-between p-6 bg-gray-50/50 border-b border-component-border">
            <h3 className="text-[10px] font-bold text-theme-muted uppercase tracking-[0.3em]">
              {t("navbar.notifications")}
            </h3>
            {unreadNotifications > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-[9px] font-bold text-chafadi-primary uppercase tracking-widest hover:underline"
              >
                {t("navbar.markAllRead")}
              </button>
            )}
          </div>

          <div className="max-h-[450px] overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="p-12 text-center flex flex-col items-center gap-4">
                <div className="w-8 h-8 border-2 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin" />
                <span className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">
                  {t("common.loading")}...
                </span>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className="flex items-start gap-4 p-5 hover:bg-theme-bg cursor-pointer group border-b border-component-border last:border-0 transition-all"
                  onClick={() => handleNotificationSelect(n)}
                >
                  <div className="shrink-0 w-12 h-12 rounded-2xl overflow-hidden bg-theme-bg flex items-center justify-center border border-component-border shadow-inner">
                    {n.image_url ? (
                      <img src={n.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <FiActivity className="text-chafadi-primary text-xl" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-theme-text font-bold leading-relaxed mb-2 line-clamp-2">
                      {n.message}
                    </p>
                    <span className="text-[9px] font-bold text-theme-muted uppercase tracking-widest opacity-60">
                      {new Date(n.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-theme-muted hover:bg-rose-50 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteNotification(n.id);
                    }}
                  >
                    <FiX size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="p-16 text-center space-y-4 text-theme-muted">
                <div className="w-16 h-16 bg-theme-bg rounded-full flex items-center justify-center mx-auto opacity-20">
                  <FiBell size={32} />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">
                  {t("navbar.noNewNotifications")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SearchBar = ({
  searchTerm,
  setSearchTerm,
  handleSearchSubmit,
  showSuggestions,
  setShowSuggestions,
  suggestions,
  handleSuggestionClick,
  currency,
  rates,
}) => {
  const { t, i18n } = useTranslation();
  const searchBarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        if (setShowSuggestions) setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [setShowSuggestions]);

  return (
    <div ref={searchBarRef} className="relative w-full group">
      <form onSubmit={handleSearchSubmit} className="relative flex items-center">
        <div className="relative flex-1">
          <input
            type="text"
            className="w-full h-11 pl-11 pr-20 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full text-[13px] font-medium text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:bg-[var(--color-bg)] focus:ring-2 focus:ring-[var(--color-accent)]/25 focus:border-[var(--color-accent)] transition-all shadow-2xs"
            placeholder={t("navbar.searchPlaceholder", "Search for products...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => {
              if (searchTerm.trim().length >= 2 && setShowSuggestions) {
                setShowSuggestions(true);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && setShowSuggestions) {
                setShowSuggestions(false);
              }
            }}
            aria-label={t("navbar.searchPlaceholder", "Search for products")}
          />
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)] group-focus-within:text-[var(--color-accent)] transition-colors text-base pointer-events-none" />

          {/* Instant Clear Button */}
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                if (setShowSuggestions) setShowSuggestions(false);
              }}
              className="absolute right-11 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-border)]/50 flex items-center justify-center transition-colors"
              aria-label="Clear search"
            >
              <FiX size={13} />
            </button>
          )}
        </div>

        {/* Orange Search Action Button */}
        <button
          type="submit"
          aria-label={t("navbar.search", "Search")}
          title={t("navbar.search", "Search")}
          className="absolute right-1.5 w-8 h-8 rounded-full bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white flex items-center justify-center shadow-sm active:scale-90 transition-all cursor-pointer"
        >
          <FiArrowRight size={14} />
        </button>
      </form>

      {/* ── REDESIGNED APPLE SEARCH DROPDOWN ── */}
      {showSuggestions && (
        <>
          {suggestions.length > 0 ? (
            <div className="absolute top-full left-0 right-0 mt-2 z-[1100] bg-[var(--glass-bg)] backdrop-blur-2xl border border-[var(--color-border)] shadow-[0_20px_60px_rgba(0,0,0,0.14)] rounded-[22px] overflow-hidden animate-fade-in">
              {/* Header Bar */}
              <div className="px-4 py-2.5 bg-[var(--color-bg-secondary)]/70 border-b border-[var(--color-border)]/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[var(--color-accent)] animate-pulse" />
                  <span className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                    {t("navbar.suggestions", "Instant Matches")}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text-tertiary)]">
                    {suggestions.length}
                  </span>
                </div>
                <span className="text-[10px] font-medium text-[var(--color-text-tertiary)] hidden sm:inline">
                  {t("navbar.pressEnterHint", "Click to view product or press ↵")}
                </span>
              </div>

              {/* Suggestions List */}
              <div className="p-2 space-y-1 max-h-[340px] overflow-y-auto no-scrollbar">
                {suggestions.map((product) => {
                  const stock = formatStockDisplay(product.quantity_in_stock, i18n.language);
                  const dateAdded = formatProductAddedDate(product.created_at, i18n.language);
                  return (
                    <div
                      key={product.id}
                      className="group/item flex items-center justify-between p-2.5 hover:bg-[var(--color-bg-secondary)] active:bg-[var(--color-accent-subtle)] rounded-[16px] cursor-pointer transition-all gap-3"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSuggestionClick(product.id);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        handleSuggestionClick(product.id);
                      }}
                      onClick={() => handleSuggestionClick(product.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-12 h-12 rounded-[14px] border border-[var(--color-border)] overflow-hidden bg-[var(--color-bg-secondary)] shrink-0 group-hover/item:border-[var(--color-accent)]/50 transition-colors">
                          <img
                            src={product.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover group-hover/item:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-[var(--color-text-primary)] group-hover/item:text-[var(--color-accent)] transition-colors truncate leading-tight">
                            {localizedProductName(product, i18n.language)}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs font-bold text-[var(--color-accent)]">
                              {formatPrice(
                                product.price || product.max_price || product.min_price,
                                currency,
                                rates
                              )}
                            </span>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${stock.badgeColor}`}>
                              {stock.text}
                            </span>
                            {product.category && (
                              <>
                                <span className="text-[10px] text-[var(--color-text-tertiary)]">•</span>
                                <span className="text-[11px] font-normal text-[var(--color-text-tertiary)] truncate">
                                  {localizedCategoryName(product.category, i18n.language)}
                                </span>
                              </>
                            )}
                            {dateAdded && (
                              <>
                                <span className="text-[10px] text-[var(--color-text-tertiary)] hidden sm:inline">•</span>
                                <span className="text-[10px] font-medium text-[var(--color-text-tertiary)] hidden sm:inline">
                                  {dateAdded}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="w-7 h-7 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] group-hover/item:bg-[var(--color-accent)] group-hover/item:text-white group-hover/item:border-transparent text-[var(--color-text-tertiary)] flex items-center justify-center shrink-0 transition-all shadow-2xs">
                        <FiChevronRight size={14} className="group-hover/item:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom Action Bar ("Press search button to show results") */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleSearchSubmit(e);
                }}
                className="p-3 bg-[var(--color-bg-secondary)]/90 hover:bg-[var(--color-accent-subtle)] border-t border-[var(--color-border)] flex items-center justify-between cursor-pointer transition-all group/all"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[var(--color-accent)] text-white flex items-center justify-center shrink-0 shadow-xs group-hover/all:scale-110 transition-transform">
                    <FiSearch size={12} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[var(--color-text-secondary)] truncate">
                      {t("navbar.viewAllResults", "See all results for")}{" "}
                      <span className="font-bold text-[var(--color-text-primary)] group-hover/all:text-[var(--color-accent)] transition-colors">
                        "{searchTerm}"
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-bold text-[var(--color-accent)] shrink-0 pl-2">
                  <span>{t("navbar.showResults", "Show Results")}</span>
                  <FiArrowRight size={13} className="group-hover/all:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ) : (
            searchTerm.trim().length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 z-[1100] bg-[var(--glass-bg)] backdrop-blur-2xl border border-[var(--color-border)] shadow-[0_20px_60px_rgba(0,0,0,0.14)] rounded-[22px] p-5 text-center space-y-2.5 animate-fade-in">
                <div className="w-10 h-10 mx-auto rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-tertiary)] flex items-center justify-center">
                  <FiSearch size={18} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-primary)]">
                    {t("navbar.noInstantMatches", "No direct preview matches")}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-secondary)] mt-0.5">
                    {t("navbar.pressToExplore", "Press the search button to explore all listings for")}{" "}
                    <span className="font-bold text-[var(--color-text-primary)]">"{searchTerm}"</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSearchSubmit(e);
                  }}
                  className="btn-primary h-9 px-5 text-xs font-bold inline-flex items-center gap-2 shadow-sm active:scale-95"
                >
                  <FiSearch size={13} />
                  <span>{t("navbar.showResults", "Show Results")}</span>
                </button>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

const AppNavbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();

  const { currency, rates } = useCurrency();
  const { cartCount, favoritesCount } = useAppData();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const profileRef = useRef(null);
  const categoriesRef = useRef(null);

  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const controlNavbar = () => {
      if (typeof window !== "undefined") {
        if (window.scrollY > lastScrollY && window.scrollY > 150) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
        setLastScrollY(window.scrollY);
      }
    };
    window.addEventListener("scroll", controlNavbar);
    return () => window.removeEventListener("scroll", controlNavbar);
  }, [lastScrollY]);



  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideCategories = !categoriesRef.current?.contains(event.target);
      const clickedOutsideProfile = !profileRef.current?.contains(event.target);

      if (clickedOutsideCategories && clickedOutsideProfile) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get("/categories/");
        const allCats = res.data.results || res.data;
        const roots = allCats.filter((c) => !c.parent_id);
        roots.forEach((root) => {
          root.children = allCats.filter((c) => c.parent_id === root.id);
        });
        setCategories(roots);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchTerm.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/products/?search=${searchTerm}&limit=5`);
        setSuggestions(res.data.results || res.data);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Search suggestion fetch failed", error);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);



  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleSearchSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setShowSuggestions(false);
    const query = searchTerm.trim();
    if (query) {
      navigate(`/search/${encodeURIComponent(query)}`);
    } else {
      navigate("/search/");
    }
  };

  const handleSuggestionClick = (productId) => {
    setShowSuggestions(false);
    setSearchTerm("");
    navigate(`/products/${productId}`);
  };

  const handleNavClick = () => {
    setActiveDropdown(null);
  };





  const profileImage =
    user?.supplier_profile?.logo_url ||
    user?.buyer_profile?.logo_url ||
    `https://ui-avatars.com/api/?name=${user?.first_name || "C"}+${user?.last_name || "U"}&background=ff7a00&color=fff&size=100`;

  return (
    <>
      <header
        className={`sticky top-0 z-[1000] w-full nav-glass transition-transform duration-300 ease-[cubic-bezier(0.28,0.11,0.32,1)] ${
          isVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">

          {/* ── TIER 1: BRAND + SEARCH + ACTIONS ── */}
          <div className="flex items-center justify-between h-14 md:h-16 gap-4 lg:gap-6">

            {/* ── MOBILE HAMBURGER (LEFT, before logo) ── */}
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              className="lg:hidden shrink-0 w-10 h-10 flex items-center justify-center text-[var(--color-text-primary)] bg-[var(--color-bg-secondary)] rounded-full border border-[var(--color-border)] active:scale-95 transition-all"
            >
              <FiMenu size={18} />
            </button>

            {/* ── LOGO ── */}
            <Link
              to="/"
              onClick={handleNavClick}
              className="shrink-0 no-underline group flex items-center h-10"
            >
              <img
                src="/logo.png"
                alt="Chafadi"
                className="h-9 w-auto object-contain group-hover:opacity-85 transition-opacity duration-200"
              />
            </Link>

          {/* ── SEARCH (Desktop) ── */}
          <div className="hidden lg:block flex-1 max-w-xl">
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSearchSubmit={handleSearchSubmit}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              suggestions={suggestions}
              handleSuggestionClick={handleSuggestionClick}
              currency={currency}
              rates={rates}
            />
          </div>

          {/* ── DESKTOP ACTIONS ── */}
          <div className="hidden lg:flex items-center gap-2.5">
            <LanguageSwitcher />

            {/* Favorites */}
            <Link
              to="/favorites"
              className="w-10 h-10 flex items-center justify-center bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full text-[var(--color-text-primary)] hover:border-[var(--color-accent)] transition-all relative no-underline active:scale-95"
            >
              <FiHeart size={17} />
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[17px] h-[17px] px-1 bg-[var(--color-accent)] text-white text-[9px] font-semibold rounded-full">
                  {favoritesCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="w-10 h-10 flex items-center justify-center bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full text-[var(--color-text-primary)] hover:border-[var(--color-accent)] transition-all relative no-underline active:scale-95"
            >
              <FiShoppingCart size={17} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[17px] h-[17px] px-1 bg-[var(--color-accent)] text-white text-[9px] font-semibold rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Notifications */}
            <NavbarNotifications />

            {/* Avatar / Login */}
            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() =>
                    setActiveDropdown(activeDropdown === "profile" ? null : "profile")
                  }
                  className="w-10 h-10 rounded-full overflow-hidden border border-[var(--color-border)] hover:border-[var(--color-accent)] transition-all active:scale-95"
                >
                  <img src={profileImage} alt="" className="w-full h-full object-cover" />
                </button>

                {activeDropdown === "profile" && (
                  <div className="absolute top-full right-0 mt-3 w-64 bg-[var(--color-bg)] rounded-[22px] shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-[var(--color-border)] py-3 z-[1000] animate-fade-in overflow-hidden">
                    <div className="px-5 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
                      <p className="text-[10px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider mb-0.5">
                        {t("navbar.authenticated")}
                      </p>
                      <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">
                        {user.first_name} {user.last_name}
                      </p>
                    </div>
                    <div className="p-2 space-y-0.5">
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-2.5 text-[12px] font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] no-underline rounded-xl transition-all"
                        onClick={() => setActiveDropdown(null)}
                      >
                        <FiUser size={15} className="text-[var(--color-accent)]" /> {t("navbar.profile")}
                      </Link>
                      <Link
                        to="/favorites"
                        className="flex items-center gap-3 px-4 py-2.5 text-[12px] font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] no-underline rounded-xl transition-all"
                        onClick={() => setActiveDropdown(null)}
                      >
                        <FiHeart size={15} className="text-[var(--color-accent)]" /> {t("navbar.favorites")}
                      </Link>
                      <Link
                        to="/chat/all"
                        className="flex items-center gap-3 px-4 py-2.5 text-[12px] font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] no-underline rounded-xl transition-all"
                        onClick={() => setActiveDropdown(null)}
                      >
                        <FiMessageSquare size={15} className="text-[var(--color-accent)]" /> {t("navbar.myMessages")}
                      </Link>
                    </div>
                    <div className="my-1 border-t border-[var(--color-border)]" />
                    <div className="p-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-[12px] font-medium text-[var(--color-error)] hover:bg-[var(--color-bg-secondary)] rounded-xl transition-all"
                      >
                        <FiLogOut size={15} /> {t("navbar.logout")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-secondary h-9 px-5 text-[13px]"
              >
                {t("navbar.login")}
              </Link>
            )}

            {/* Sell CTA */}
            <Link
              to="/products/add"
              className="btn-primary h-9 px-5 text-[13px] gap-1.5"
            >
              <FiPlusCircle size={15} /> {t("navbar.sell")}
            </Link>
          </div>

          {/* ── MOBILE RIGHT SIDE: Cart icon only ── */}
          <div className="lg:hidden flex items-center gap-2">
            <Link
              to="/cart"
              className="w-10 h-10 flex items-center justify-center bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full text-[var(--color-text-primary)] relative no-underline active:scale-95 transition-all"
            >
              <FiShoppingCart size={17} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[17px] h-[17px] px-1 bg-[var(--color-accent)] text-white text-[9px] font-semibold rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* ── TIER 2: DESKTOP NAV LINKS ── */}
        <div className="hidden lg:flex items-center justify-between h-11 border-t border-[var(--color-border)] relative">
          <nav className="flex items-center gap-1 py-1 overflow-visible">
            <Link
              to="/search/"
              className="text-[12px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors tracking-tight no-underline px-3.5 py-1.5 rounded-full hover:bg-[var(--color-bg-secondary)] shrink-0"
            >
              {t("navbar.shop")}
            </Link>

            <Link
              to="/new-arrivals"
              className="text-[12px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors tracking-tight no-underline px-3.5 py-1.5 rounded-full hover:bg-[var(--color-bg-secondary)] shrink-0 flex items-center gap-1.5"
            >
              {t("navbar.newArrivals")}
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-accent)]" />
            </Link>

            <Link
              to="/flash-deals"
              className="text-[12px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors tracking-tight no-underline px-3.5 py-1.5 rounded-full hover:bg-[var(--color-bg-secondary)] shrink-0 flex items-center gap-1"
            >
              <FiZap size={13} className="text-amber-500" />
              {t("navbar.flashDeals")}
            </Link>

            {categories.length > 0 && (
              <>
                <div className="h-3.5 w-[1px] bg-[var(--color-border)] mx-1 shrink-0" />

                {/* Top Categories directly in Tier 2 */}
                {categories.slice(0, 5).map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}/${encodeURIComponent(localizedCategoryName(category, i18n.language))}`}
                    className="text-[12px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors tracking-tight no-underline px-3 py-1.5 rounded-full hover:bg-[var(--color-bg-secondary)] shrink-0 whitespace-nowrap"
                  >
                    {localizedCategoryName(category, i18n.language)}
                  </Link>
                ))}

                {/* All Categories Dropdown Trigger & Compact Menu */}
                <div ref={categoriesRef} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveDropdown((prev) => (prev === "categories" ? null : "categories"));
                    }}
                    className={`flex items-center gap-1.5 text-[12px] font-semibold tracking-tight px-3.5 py-1.5 rounded-full transition-all cursor-pointer ${
                      activeDropdown === "categories"
                        ? "bg-[var(--color-accent)] text-white shadow-sm"
                        : "text-[var(--color-accent)] hover:bg-[var(--color-bg-secondary)]"
                    }`}
                  >
                    <FiGrid size={12} />
                    <span>{t("navbar.allCategories", "All Categories")}</span>
                    <FiChevronDown
                      className={`transition-transform duration-200 ${
                        activeDropdown === "categories" ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Categories Dropdown: 100% OPAQUE background, anchored right below the button */}
                  {activeDropdown === "categories" && (
                    <div className="absolute top-full right-0 z-[9999] mt-2 w-[540px] max-w-[90vw] rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e] p-5 shadow-[0_25px_60px_rgba(0,0,0,0.25)] animate-fade-in">
                      {/* Top Header */}
                      <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100 dark:border-white/10">
                        <div className="flex items-center gap-2">
                          <FiGrid className="text-[var(--color-accent)] text-sm" />
                          <span className="text-xs font-extrabold text-gray-900 dark:text-white uppercase tracking-wider">
                            {t("navbar.allCategories", "All Categories")}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">
                            {categories.length}
                          </span>
                        </div>

                        <Link
                          to="/search/"
                          onClick={() => setActiveDropdown(null)}
                          className="text-xs font-bold text-[var(--color-accent)] hover:underline flex items-center gap-1"
                        >
                          <span>Explore All</span>
                          <FiArrowRight size={12} />
                        </Link>
                      </div>

                      {/* Categories Grid (2 Columns, Clean, Opaque) */}
                      <div className="grid max-h-[55vh] grid-cols-2 gap-x-6 gap-y-4 overflow-y-auto pr-1">
                        {categories.map((category) => (
                          <div key={category.id} className="space-y-1.5">
                            <Link
                              to={`/category/${category.id}/${encodeURIComponent(localizedCategoryName(category, i18n.language))}`}
                              className="group flex items-center justify-between font-bold text-gray-900 dark:text-white hover:text-[var(--color-accent)] transition-colors no-underline tracking-tight text-[13px] pb-1 border-b border-gray-100 dark:border-white/5"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <span className="group-hover:translate-x-0.5 transition-transform truncate">
                                {localizedCategoryName(category, i18n.language)}
                              </span>
                              <FiChevronRight size={11} className="opacity-0 group-hover:opacity-100 text-[var(--color-accent)] transition-opacity shrink-0" />
                            </Link>

                            {category.children && category.children.length > 0 && (
                              <div className="flex flex-col gap-1 pl-1">
                                {category.children.map((child) => (
                                  <Link
                                    key={child.id}
                                    to={`/category/${child.id}/${encodeURIComponent(localizedCategoryName(child, i18n.language))}`}
                                    className="text-[12px] font-normal text-gray-500 dark:text-gray-400 hover:text-[var(--color-accent)] hover:translate-x-0.5 transition-all no-underline tracking-tight truncate"
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    {localizedCategoryName(child, i18n.language)}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>

          {/* Right Utility Badge */}
          <div className="hidden xl:flex items-center gap-3 shrink-0 pl-4 text-[11px] font-semibold text-[var(--color-text-secondary)]">
            <span className="flex items-center gap-1.5 px-3 py-1 bg-[var(--color-bg-secondary)] rounded-full border border-[var(--color-border)]">
              <span>🇳🇪</span> Fast Delivery in Niger
            </span>
          </div>
        </div>

        {/* ── MOBILE SEARCH BAR ── */}
        <div className="lg:hidden pb-4">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleSearchSubmit={handleSearchSubmit}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            suggestions={suggestions}
            handleSuggestionClick={handleSuggestionClick}
            currency={currency}
            rates={rates}
          />
        </div>
      </div>

    </header>
      <MobileDrawer
        isOpen={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        categories={categories}
      />
    </>
  );
};

export default AppNavbar;
